import { useState, useMemo, useEffect, useRef } from 'react';

interface UsePaginationProps<T> {
  data: T[];
  itemsPerPage?: number;
}

interface UsePaginationReturn<T> {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  paginatedData: T[];
  showingFrom: number;
  showingTo: number;
  totalItems: number;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  setItemsPerPage: (items: number) => void;
}

export function usePagination<T>({ 
  data, 
  itemsPerPage: initialItemsPerPage = 10 
}: UsePaginationProps<T>): UsePaginationReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPageState] = useState(initialItemsPerPage);

  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to page 1 when data changes (e.g., filter applied)
  const prevDataLengthRef = useRef(totalItems);
  useEffect(() => {
    if (prevDataLengthRef.current !== totalItems) {
      setCurrentPage(1);
      prevDataLengthRef.current = totalItems;
    }
  }, [totalItems]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  }, [data, currentPage, itemsPerPage]);

  const showingFrom = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingTo = Math.min(currentPage * itemsPerPage, totalItems);

  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setItemsPerPage = (items: number) => {
    setItemsPerPageState(items);
    // Adjust current page if it would be out of bounds with new items per page
    const newTotalPages = Math.ceil(totalItems / items);
    if (currentPage > newTotalPages) {
      setCurrentPage(1);
    }
  };

  return {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    showingFrom,
    showingTo,
    totalItems,
    goToPage,
    nextPage,
    prevPage,
    setItemsPerPage
  };
}